import PolicyShell, { Section, SubHead, Bullets } from "@/components/PolicyShell";

const STEPS = [
  {
    n: "01",
    title: "Submit Your Request",
    body:
      "Log into your VIP account and navigate to the Renewal Portal. Select your previously purchased jacket from your order history, upload 3–5 clear photos (front, back, interior, hardware close-ups), and select its current condition. Then browse our collection and choose the new jacket you wish to upgrade to.",
  },
  {
    n: "02",
    title: "Get Your Instant Estimate",
    body:
      "Our intelligent automated valuation system will analyze your photos, condition rating, and jacket model to generate an Estimated Trade-In Value instantly. This estimate is preliminary and subject to physical verification at our facility. You may accept or decline at this stage with no obligation.",
  },
  {
    n: "03",
    title: "Ship Your Jacket to Us",
    body:
      "Accept the estimate to receive a prepaid shipping label and packing instructions via email. Safely pack your jacket in its original box (if available) or sturdy packaging. Drop off at any authorized carrier location. Tracking information will be provided. Shipping to our USA inspection facility is covered by BOSCAGE.",
  },
  {
    n: "04",
    title: "Final Inspection & Upgrade",
    body:
      "Upon receipt, our Leather Quality Experts conduct a thorough physical inspection within 3 business days. The final trade-in value is confirmed (or adjusted based on actual condition) and instantly credited to your account as a store credit or discount code. Use it immediately to complete your new BOSCAGE jacket purchase!",
  },
];

const FAQ = [
  {
    q: "Can I trade in a jacket I purchased as a gift or received secondhand?",
    a:
      "No. The jacket must be linked to your own VIP account via its original Order Number. Jackets purchased by someone else or acquired through secondary markets are not eligible, even if they are genuine BOSCAGE products.",
  },
  {
    q: "What if the final inspection value differs from my online estimate?",
    a:
      "The online estimate is generated automatically based on your inputs and photos. After physical inspection, our experts may adjust the value based on actual condition. You will be notified of any change and can choose to proceed or have your jacket returned at your expense.",
  },
  {
    q: "How long does the entire process take from submission to credit?",
    a:
      "Typically 7–10 business days total: 1–2 days for shipping, 3 business days for inspection, and same-day credit issuance upon approval. You will receive email updates at every stage.",
  },
  {
    q: "Can I use my store credit on any jacket in the collection?",
    a:
      "Store credit applies to any full-price BOSCAGE leather jacket available at the time of purchase. Exclusions may apply to select limited-edition releases, which will be clearly marked on the product page.",
  },
  {
    q: "What happens if my jacket is rejected after inspection?",
    a:
      "If your jacket does not meet the minimum condition requirements, BOSCAGE will notify you within 3 business days of inspection. You may choose to have it returned (at your cost) or authorize disposal. No credit will be issued for rejected jackets.",
  },
  {
    q: "Is there a minimum trade-in value?",
    a:
      "Yes. A minimum estimated value threshold applies for the trade-in to proceed. If the jacket's value falls below this threshold, you will be notified and may withdraw your submission at no cost before shipping.",
  },
];

export default function TradeInProgramPage() {
  return (
    <PolicyShell
      testId="trade-in-page"
      overline="BOSCAGE ELEGANCE · VIP EXCLUSIVE"
      title="The BOSCAGE Renewal Program"
      contact="Refresh Your Style. Renew Your Legacy."
      lastUpdated="Policy Last Updated: June 2025. BOSCAGE Elegance reserves the right to modify program terms with 30 days notice to active VIP members."
    >
      <p>
        At BOSCAGE Elegance, we believe that premium leather tells a story &mdash; of adventure, sophistication, and
        timeless style. As your journey evolves, so should your wardrobe. The BOSCAGE Renewal Program is our exclusive
        trade-in initiative, available only to registered VIP Members. Trade in your previously loved BOSCAGE leather
        jacket and receive store credit toward a brand-new masterpiece from our latest collection.
      </p>

      <Section title="How It Works">
        <p className="italic text-[#000000]/65">Four simple steps to upgrade your legacy.</p>
        <div className="grid sm:grid-cols-2 gap-6 mt-2" data-testid="renewal-steps">
          {STEPS.map((s) => (
            <article key={s.n} className="border border-[#000000]/12 p-5">
              <p className="font-serif text-3xl text-[#c9a84c]">{s.n}</p>
              <h3 className="font-serif text-lg font-semibold text-[#c9a84c] mt-2">{s.title}</h3>
              <p className="text-sm text-[#000000]/70 mt-3 leading-relaxed">{s.body}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section title="Program Eligibility & Rules">
        <p>
          To maintain the high standards of our brand and community, the Renewal Program is governed by the following
          guidelines.
        </p>
        <SubHead>Exclusive VIP Access</SubHead>
        <p>
          This program is strictly available to registered BOSCAGE VIP Members. Non-members must complete VIP
          registration before initiating any trade-in request. Membership is free and can be completed on our website.
        </p>
        <SubHead>Timeframe Limit (36 Months)</SubHead>
        <p>
          Only BOSCAGE leather jackets purchased within the last 3 years (36 months) from the date of trade-in
          submission are eligible. Purchases beyond this window cannot be accepted into the program.
        </p>
        <SubHead>Proof of Purchase Required</SubHead>
        <p>
          A valid original Order Number linked to your registered VIP account is mandatory. Orders not associated with
          your account or purchased through unauthorized resellers are not eligible.
        </p>
        <SubHead>One Active Trade-In at a Time</SubHead>
        <p>
          Each VIP member may have only one active trade-in request open at any given time. A new submission may be
          initiated once the current trade-in is fully resolved (approved, rejected, or withdrawn).
        </p>
        <SubHead>Store Credit Only — No Cash Payouts</SubHead>
        <p>
          All trade-in values are issued exclusively as BOSCAGE Store Credit or Discount Codes, redeemable toward a
          new jacket purchase only. No cash payouts, bank transfers, gift cards, or refunds are provided under this
          program.
        </p>
        <SubHead>Program Abuse Policy</SubHead>
        <p>
          BOSCAGE reserves the right to suspend or permanently revoke VIP membership and program access in cases of
          fraudulent submissions, misrepresentation of jacket condition, or repeated program misuse.
        </p>
      </Section>

      <Section title="Jacket Condition Guidelines">
        <p>
          Rate your jacket honestly when submitting. Misrepresentation of condition may result in a revised final
          value after physical inspection, or disqualification from the program.
        </p>

        <div className="grid sm:grid-cols-3 gap-5">
          <article className="border border-[#000000]/12 p-5">
            <p className="overline-gold">Like New</p>
            <ul className="mt-3 text-sm text-[#000000]/75 space-y-2">
              <li><strong className="text-[#000000]">Leather Surface:</strong> Flawless, no scratches, scuffs, fading, or discoloration.</li>
              <li><strong className="text-[#000000]">Inner Lining:</strong> Pristine condition, no tears, stains, or loose stitching.</li>
              <li><strong className="text-[#000000]">Hardware:</strong> All zippers, buttons, snaps fully functional and blemish-free.</li>
              <li><strong className="text-[#000000]">Overall:</strong> Jacket appears virtually unused. Minimal to zero signs of wear.</li>
            </ul>
          </article>
          <article className="border border-[#000000]/12 p-5">
            <p className="overline-gold">Good</p>
            <ul className="mt-3 text-sm text-[#000000]/75 space-y-2">
              <li><strong className="text-[#000000]">Leather Surface:</strong> Minor natural aging or patina acceptable. No deep scratches or tears.</li>
              <li><strong className="text-[#000000]">Inner Lining:</strong> Intact with possible light wear marks. No significant staining or damage.</li>
              <li><strong className="text-[#000000]">Hardware:</strong> All components fully functional. Minor surface marks acceptable.</li>
              <li><strong className="text-[#000000]">Overall:</strong> Shows signs of careful regular use but remains in very presentable condition.</li>
            </ul>
          </article>
          <article className="border border-[#000000]/12 p-5">
            <p className="overline-gold">Fair</p>
            <ul className="mt-3 text-sm text-[#000000]/75 space-y-2">
              <li><strong className="text-[#000000]">Leather Surface:</strong> Noticeable wear, minor scuffs, or surface marks present.</li>
              <li><strong className="text-[#000000]">Inner Lining:</strong> May show moderate wear but structurally intact.</li>
              <li><strong className="text-[#000000]">Hardware:</strong> Fully functional. No missing components.</li>
              <li><strong className="text-[#000000]">Overall:</strong> Visibly worn but retains structural integrity. No major damage.</li>
            </ul>
          </article>
        </div>

        <p className="text-sm text-[#000000]/65 italic border-l-2 border-[#000000]/50 pl-4 py-1">
          Not Eligible: Jackets with severe structural damage, torn/missing hardware, heavy or persistent odors,
          mold/mildew, major staining, or alterations not performed by BOSCAGE are disqualified. Such jackets will be
          returned at the customer&apos;s shipping expense after inspection.
        </p>
      </Section>

      <Section title="Store Credit & Discount Code Terms">
        <SubHead>Validity Period</SubHead>
        <p>
          Store credit and discount codes issued through the Renewal Program are valid for 12 months from the date of
          issuance. Credits not used within this period will expire and cannot be reinstated.
        </p>
        <SubHead>Non-Transferable</SubHead>
        <p>
          Credits and discount codes are strictly non-transferable. They may only be used by the VIP account holder
          who initiated the trade-in. Selling, gifting, or sharing of codes is prohibited.
        </p>
        <SubHead>Single Use Per Transaction</SubHead>
        <p>
          Discount codes are single-use only. Store credit may be applied in multiple transactions until the balance
          is exhausted within the validity period.
        </p>
        <SubHead>Applicable Purchases Only</SubHead>
        <p>
          Credits are redeemable only toward the purchase of a new full-price BOSCAGE leather jacket. They cannot be
          applied to accessories, limited editions (unless stated), shipping fees, or taxes.
        </p>
        <SubHead>No Cash Equivalent</SubHead>
        <p>
          Store credit holds no cash value and cannot be exchanged, redeemed, or refunded for cash, cheque, or any
          monetary transfer under any circumstances.
        </p>
        <SubHead>Combining Offers</SubHead>
        <p>
          Store credit from the Renewal Program may not be combined with other promotional discount codes or sale
          pricing unless explicitly permitted at the time of redemption.
        </p>
      </Section>

      <Section title="Frequently Asked Questions">
        <div className="space-y-5">
          {FAQ.map((f, i) => (
            <details
              key={i}
              data-testid={`renewal-faq-${i}`}
              className="border border-[#000000]/12 p-5 group"
            >
              <summary className="cursor-pointer font-serif text-lg text-[#000000] flex items-start gap-3 list-none">
                <span className="text-[#c9a84c] font-semibold shrink-0">Q.</span>
                <span>{f.q}</span>
              </summary>
              <p className="mt-3 text-sm text-[#000000]/75 leading-relaxed pl-7">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      <Section title="Contact & Support">
        <SubHead>Renewal Portal Access</SubHead>
        <Bullets
          items={[
            "VIP Concierge (Live Chat) — Available Mon–Fri, 9 AM – 7 PM EST. Instant support via your VIP account portal.",
            "Email Support — renewal@boscageelegance.com. Response within 24 business hours.",
            "USA Inspection Facility — All jackets are physically inspected at our authorized USA-based quality control center. Full address provided with your shipping label.",
          ]}
        />
        <p className="text-sm text-[#000000]/70 italic pt-2">
          Ready to Upgrade? Your next favorite leather jacket is waiting. Log in &amp; start your renewal process.
        </p>
        <p className="text-[10px] tracking-[0.18em] uppercase text-[#000000]/45">
          © BOSCAGE Elegance — VIP Renewal Program Policy · Store credit is non-transferable and expires 12 months
          from issuance. Program terms subject to change with 30 days notice. For full Terms &amp; Conditions visit
          boscageelegance.com/vip-terms
        </p>
      </Section>
    </PolicyShell>
  );
}
